import { Button } from '@creditornot/cb-button';
import { Pencil } from '@creditornot/cb-icons';
import { cssThemedValue } from '@creditornot/cb-ingredients';
import { InputSwitch } from '@creditornot/cb-switch';
import { Tag } from '@creditornot/cb-tag';
import React, { useState, useCallback } from 'react';
import { Form, Field } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Typography as T } from 'common/typography';

import { VisualSetting } from './components/VisualSetting';

type Props = {
  className?: string;
  editable: boolean;
  values: VisualSettingsFormData;
  onSubmit: (values: VisualSettingsFormData) => void;
};

type VisualSettingsFormData = {
  useCategoryListLayout: boolean;
};

const Header = styled.div`
  position: relative;
`;

const ActionContainer = styled.div`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
`;

const Title = styled(T.Heading6)`
  color: ${cssThemedValue('textPrimary')};
  display: flex;
  margin-block-end: 24px;
  position: relative;
`;

const EditButton = styled(Button).attrs({ size: 'small', variant: 'lightBlue', icon: <Pencil /> })``;

const CancelButton = styled(Button).attrs({ size: 'small', variant: 'lightBlue' })``;

const SubmitButton = styled(Button).attrs({ size: 'small', variant: 'blue' })`
  margin-inline-start: 8px;
`;

const FormButtonContainer = styled.div``;

const Root = styled.div``;

export const VisualSettingsForm = ({ className, values, editable, onSubmit }: Props) => {
  const { formatMessage } = useIntl();
  const [editing, setEditing] = useState(false);

  const handleEdit = useCallback(() => setEditing(true), []);

  return (
    <Root className={className}>
      <Form<VisualSettingsFormData> onSubmit={onSubmit} initialValues={values}>
        {({ handleSubmit, pristine, submitting, form: { reset } }) => (
          <>
            <Header>
              <Title>
                <FormattedMessage id="venue-details.visual-settings.title-retail" />
              </Title>

              {editable && (
                <ActionContainer data-testid="VisualSettingsFormActions">
                  {editing ? (
                    <FormButtonContainer>
                      <CancelButton
                        disabled={submitting}
                        onClick={() => {
                          reset();
                          setEditing(false);
                        }}
                      >
                        <FormattedMessage id="state.cancel" />
                      </CancelButton>
                      <SubmitButton disabled={pristine || submitting} onClick={handleSubmit}>
                        <FormattedMessage id="state.save" />
                      </SubmitButton>
                    </FormButtonContainer>
                  ) : (
                    <EditButton onClick={handleEdit}>
                      <FormattedMessage id="state.edit" />
                    </EditButton>
                  )}
                </ActionContainer>
              )}
            </Header>

            <VisualSetting
              title={formatMessage({ id: 'venue-details.visual-settings.category-navigation.label' })}
              description={formatMessage({ id: 'venue-details.visual-settings.category-navigation.description' })}
            >
              <Field<VisualSettingsFormData['useCategoryListLayout']> type="checkbox" name="useCategoryListLayout">
                {({ input }) =>
                  editing && editable ? (
                    <InputSwitch
                      checked={input.checked}
                      onChange={event => input.onChange(event.currentTarget.checked)}
                    />
                  ) : (
                    <Tag variant="lightBlue" size="small">
                      <FormattedMessage
                        id={input.checked ? 'venue-details.visual-settings.yes' : 'venue-details.visual-settings.no'}
                      />
                    </Tag>
                  )
                }
              </Field>
            </VisualSetting>
          </>
        )}
      </Form>
    </Root>
  );
};
